import { useState } from "react";
import { Loader2, CheckCircle, XCircle, ArrowRight, ArrowLeft, Wifi, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { registerKitService, ConnectivityCheckResult } from "@/api/services/registerKitService";
import { getErrorMessage } from "@/utils/errorUtils";

// KX-33 / T-26: live "we can see your router" connectivity probe. Sits between
// step 1 (device + vendor) and step 2 (KYC). The check is a guardrail, not a
// hard gate — RouterOS has no adapter yet (supported === false) and transient
// network issues shouldn't trap an operator, so we always allow "Continue".
export default function HotspotRegistrationConnectivity() {
  const { theme } = useApp();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const registrationId = typeof window !== "undefined" ? sessionStorage.getItem("registrationId") : null;

  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<ConnectivityCheckResult | null>(null);
  const [error, setError] = useState<string>("");
  const [hasRun, setHasRun] = useState(false);

  const runCheck = async () => {
    if (!registrationId) {
      setError(
        t("operator.registerKit.connectivity.errors.noRegistration", {
          defaultValue: "No registration in progress. Please start from step 1.",
        })
      );
      return;
    }
    setChecking(true);
    setError("");
    try {
      const res = await registerKitService.connectivityCheck(registrationId);
      setResult(res);
    } catch (e: unknown) {
      setError(
        getErrorMessage(e) ||
          t("operator.registerKit.connectivity.errors.generic", {
            defaultValue: "Couldn't run the connectivity check. Please try again.",
          })
      );
    } finally {
      setChecking(false);
      setHasRun(true);
    }
  };

  // KX-39: KYC moved to the payout boundary — activation no longer collects
  // documents, so the connectivity check leads straight to payment.
  const goNext = () => navigate("/operator/registerKitStep3");
  const goBack = () => navigate("/operator/registerKit");

  // Derive the visual state from the payload (all are HTTP 200).
  const reachable = result?.reachable === true;
  const unsupported = result !== null && result.supported === false;

  const panel =
    theme === "dark" ? "bg-[#111] border-gray-800" : "bg-white border-gray-200 shadow-xl";
  const subtext = theme === "dark" ? "text-gray-400" : "text-gray-600";

  return (
    <>
      <Helmet>
        <title>
          {t("operator.registerKit.connectivity.metaTitle", { defaultValue: "Router Check | KonectX" })}
        </title>
      </Helmet>
      <div className={`min-h-screen ${theme === "dark" ? "bg-black" : "bg-gray-50"} py-10 px-4`}>
        <div className="max-w-2xl mx-auto">
          {/* Heading */}
          <div className="mb-8">
            <h1 className={`text-2xl md:text-3xl font-bold ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
              {t("operator.registerKit.connectivity.heading", { defaultValue: "Let's find your router" })}
            </h1>
            <p className={`mt-2 text-sm ${subtext}`}>
              {t("operator.registerKit.connectivity.subtitle", {
                defaultValue: "We'll do a quick check to confirm we can reach your device before you continue.",
              })}
            </p>
          </div>

          <div className={`rounded-2xl border ${panel} p-8 md:p-10`}>
            {/* Initial / idle state */}
            {!hasRun && !checking && (
              <div className="text-center py-6">
                <Wifi className="w-12 h-12 mx-auto mb-4 text-[#2E5A88]" />
                <p className={`text-sm mb-6 ${subtext}`}>
                  {t("operator.registerKit.connectivity.idle", {
                    defaultValue: "Make sure your router is powered on and connected, then run the check.",
                  })}
                </p>
                <button
                  onClick={runCheck}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-[#2E5A88] hover:bg-[#244a73] text-white font-bold rounded-xl transition-colors"
                >
                  <Wifi className="w-5 h-5" />
                  {t("operator.registerKit.connectivity.runCheck", { defaultValue: "Run connectivity check" })}
                </button>
              </div>
            )}

            {/* Checking state */}
            {checking && (
              <div className="text-center py-10">
                <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-[#2E5A88]" />
                <p className={`text-sm ${subtext}`}>
                  {t("operator.registerKit.connectivity.checking", { defaultValue: "Looking for your router…" })}
                </p>
              </div>
            )}

            {/* Result states */}
            {!checking && hasRun && (
              <div className="py-4">
                {error ? (
                  <div className="text-center">
                    <XCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
                    <p className={`text-sm ${subtext}`}>{error}</p>
                  </div>
                ) : reachable ? (
                  <div className="text-center">
                    <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
                    <p className={`text-lg font-semibold ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                      {t("operator.registerKit.connectivity.success", { defaultValue: "We can see your router" })}
                    </p>
                    <p className={`mt-1 text-sm ${subtext}`}>
                      {t("operator.registerKit.connectivity.successBody", {
                        defaultValue: "Your device is reachable. You're good to continue.",
                      })}
                    </p>
                  </div>
                ) : unsupported ? (
                  <div className="text-center">
                    <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
                    <p className={`text-lg font-semibold ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                      {t("operator.registerKit.connectivity.unsupported", {
                        defaultValue: "We can't verify this vendor yet",
                      })}
                    </p>
                    <p className={`mt-1 text-sm ${subtext}`}>
                      {t("operator.registerKit.connectivity.unsupportedBody", {
                        defaultValue: "Automatic checks for this router brand are coming soon. You can continue for now.",
                      })}
                    </p>
                  </div>
                ) : (
                  <div className="text-center">
                    <XCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
                    <p className={`text-lg font-semibold ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                      {t("operator.registerKit.connectivity.unreachable", { defaultValue: "We couldn't reach your router" })}
                    </p>
                    <p className={`mt-1 text-sm ${subtext}`}>
                      {result?.error ||
                        t("operator.registerKit.connectivity.unreachableBody", {
                          defaultValue: "Check that it's powered on and connected, then try again.",
                        })}
                    </p>
                  </div>
                )}

                {/* Re-run option whenever the check didn't clearly succeed */}
                {(!reachable || error) && (
                  <div className="text-center mt-6">
                    <button
                      onClick={runCheck}
                      className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl border-2 font-semibold transition-colors ${
                        theme === "dark"
                          ? "border-gray-700 text-gray-200 hover:border-gray-600"
                          : "border-gray-300 text-gray-700 hover:border-[#2E5A88]"
                      }`}
                    >
                      <Wifi className="w-4 h-4" />
                      {t("operator.registerKit.connectivity.retry", { defaultValue: "Run check again" })}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200 dark:border-gray-800">
              <button
                onClick={goBack}
                className={`inline-flex items-center gap-2 px-5 py-3 rounded-xl font-semibold transition-colors ${
                  theme === "dark" ? "text-gray-300 hover:text-white" : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <ArrowLeft className="w-4 h-4" />
                {t("operator.registerKit.connectivity.back", { defaultValue: "Back" })}
              </button>
              <button
                onClick={goNext}
                className="inline-flex items-center gap-2 px-8 py-3 bg-[#2E5A88] hover:bg-[#244a73] text-white font-bold rounded-xl transition-colors"
              >
                {reachable
                  ? t("operator.registerKit.connectivity.continue", { defaultValue: "Continue to Payment" })
                  : t("operator.registerKit.connectivity.continueAnyway", { defaultValue: "Continue anyway" })}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
